/**
 * Pure viewport math. Screen coordinates are CSS pixels relative to the
 * canvas element; image coordinates are source pixels. The canvas backing
 * store is scaled by devicePixelRatio at draw time, so this transform is
 * independent of display density.
 *
 *   screen = image * scale + offset
 */
import type { Size } from "./geometry";

export interface Viewport {
  scale: number;
  tx: number;
  ty: number;
}

export interface Point {
  x: number;
  y: number;
}

export const MIN_SCALE = 0.02;
export const MAX_SCALE = 16;

export function clampScale(s: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
}

export function imageToScreen(vp: Viewport, p: Point): Point {
  return { x: p.x * vp.scale + vp.tx, y: p.y * vp.scale + vp.ty };
}

export function screenToImage(vp: Viewport, p: Point): Point {
  return { x: (p.x - vp.tx) / vp.scale, y: (p.y - vp.ty) / vp.scale };
}

/** Scale so the whole image fits, centered, with `padding` CSS px margin. */
export function fitToScreen(image: Size, container: Size, padding = 16): Viewport {
  const availW = Math.max(1, container.width - padding * 2);
  const availH = Math.max(1, container.height - padding * 2);
  const scale = clampScale(Math.min(availW / image.width, availH / image.height));
  return {
    scale,
    tx: (container.width - image.width * scale) / 2,
    ty: (container.height - image.height * scale) / 2,
  };
}

/**
 * Center a region and zoom so it occupies `fill` of the container (never past
 * MAX_SCALE, never below the fit-to-screen scale for that region's image).
 */
export function focusOn(
  region: { left: number; top: number; right: number; bottom: number },
  container: Size,
  fill = 0.5,
): Viewport {
  const w = Math.max(1, region.right - region.left);
  const h = Math.max(1, region.bottom - region.top);
  const scale = clampScale(Math.min((container.width * fill) / w, (container.height * fill) / h));
  const cx = (region.left + region.right) / 2;
  const cy = (region.top + region.bottom) / 2;
  return { scale, tx: container.width / 2 - cx * scale, ty: container.height / 2 - cy * scale };
}

/** 100% zoom, centered. */
export function resetView(image: Size, container: Size): Viewport {
  return {
    scale: 1,
    tx: (container.width - image.width) / 2,
    ty: (container.height - image.height) / 2,
  };
}

/** Zoom by `factor` keeping the image point under `anchor` (screen) fixed. */
export function zoomAt(vp: Viewport, anchor: Point, factor: number): Viewport {
  const next = clampScale(vp.scale * factor);
  const ratio = next / vp.scale;
  return {
    scale: next,
    tx: anchor.x - (anchor.x - vp.tx) * ratio,
    ty: anchor.y - (anchor.y - vp.ty) * ratio,
  };
}

export function panBy(vp: Viewport, dx: number, dy: number): Viewport {
  return { ...vp, tx: vp.tx + dx, ty: vp.ty + dy };
}

/**
 * Keep the same image point at the container center when the container is
 * resized (window resize / panel toggle) so boxes stay aligned with pixels.
 */
export function rescaleForContainer(vp: Viewport, prev: Size, next: Size): Viewport {
  const center = screenToImage(vp, { x: prev.width / 2, y: prev.height / 2 });
  return {
    scale: vp.scale,
    tx: next.width / 2 - center.x * vp.scale,
    ty: next.height / 2 - center.y * vp.scale,
  };
}

/** Screen-space distance between two screen points. */
export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
